import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FiSend, FiCopy } from 'react-icons/fi';
import ChatMessage from './ChatMessage';
import { sendChatPrompt } from '../services/apiService';
import { useAuth } from '../context/AuthContext';

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 254, 0.1);
  overflow: hidden;
  border: 0px solid rgba(0, 0, 254, 0.08);
  position: relative;
  z-index: 1;
`;

const ChatHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-weight: 600;
  color: white;
  background: linear-gradient(to right, #0a0a2e, #101042);
  display: flex;
  align-items: center;
  
  &:before {
    content: '💬';
    margin-right: 8px;
    font-size: 1.2em;
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 20px 24px;
  border-top: 1px solid #eee;
  background-color: #fff;
  background: linear-gradient(to bottom, rgba(0, 0, 254, 0.01), rgba(0, 0, 254, 0.03));
`;

const ChatInput = styled.input`
  flex: 1;
  padding: 14px 20px;
  border: 1px solid #ddd;
  border-radius: 12px;
  font-size: 16px;
  outline: none;
  background-color: #fff;
  transition: all 0.2s ease;
  
  &:focus {
    border-color: #0000fe;
    box-shadow: 0 0 0 3px rgba(0, 0, 254, 0.1);
  }
  
  &:hover:not(:disabled) {
    border-color: #6060ff;
  }
  
  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const SendButton = styled.button`
  background: linear-gradient(45deg, #0000fe, #6060ff);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 14px 24px;
  margin-left: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-weight: 500;
  position: relative;
  overflow: hidden;
  z-index: 1;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, #0000cc, #4040ff);
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 254, 0.25);
    
    &:before {
      opacity: 1;
    }
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(0, 0, 254, 0.15);
  }
  
  &:disabled {
    background: linear-gradient(45deg, #9090fe, #b0b0ff);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  svg {
    margin-right: ${props => props.children?.length > 1 ? '8px' : '0'};
    font-size: 1.1em;
  }
`;

const SuggestedPromptsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
`;

const SuggestedPrompt = styled.div`
  background-color: rgba(0, 0, 254, 0.06);
  color: #0000fe;
  padding: 10px 18px;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 254, 0.1);
  
  &:hover {
    background-color: rgba(0, 0, 254, 0.1);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 254, 0.1);
  }
`;

const suggestedPrompts = [
  "What's the balance of wallet 0x1234...?",
  "Show me transaction history for this address",
  "Who were the early buyers of SHIB token?",
  "Analyze this transaction hash",
  "Calculate profit and loss for this wallet",
  "Who were the early buyers of 5ykhiPoUTcoqpieSnQ6w2tPbCERJawe9kAM6MuZJMcbP?",
  "What's the balance of Solana wallet DssscRaCxsc1sqbJtWUXS2Gk9WU8fBf7vrMZs4aF3cHs?"
];

function ChatInterface() {
  const initialMessage = {
    id: 1,
    type: 'assistant',
    content: "Hi! I'm your blockchain data assistant. Ask me about wallets, tokens, or transactions in natural language, and I'll fetch the relevant on-chain data for you. I support both Ethereum (EVM) and Solana blockchains!"
  };

  const [messages, setMessages] = useState([initialMessage]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const { user } = useAuth();
  const userGreetedRef = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Add welcome message only once when user is available
  useEffect(() => {
    // Skip if no user or if we've already greeted them
    if (!user || userGreetedRef.current) return;
    
    // Set the flag to true to prevent multiple welcomes
    userGreetedRef.current = true;
    
    // Add the welcome message
    const welcomeMessage = {
      id: 2, // Fixed ID to avoid duplicates
      type: 'assistant',
      content: `Welcome back, ${user.email}! How can I help you today?`
    };
    
    setMessages([initialMessage, welcomeMessage]);
  }, [user]);

  const handleSendMessage = async () => {
    if (!input.trim()) return;
    
    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      content: input
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    
    try {
      // Send prompt to backend
      const response = await sendChatPrompt(input);
      
      // Create response message with the data
      const assistantMessage = {
        id: messages.length + 2,
        type: 'assistant',
        content: response.summary || 'I processed your request.',
        data: response.data,
        metadata: {
          intent: response.intent,
          entities: response.entities,
          executionTime: response.execution_time_ms
        }
      };
      
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error processing prompt:', error);
      
      // Get error details
      let errorContent = 'Sorry, I encountered an error while processing your request. Please try again.';
      
      if (error.response?.data?.error) {
        errorContent = `Error: ${error.response.data.error}`;
        console.error('Backend error details:', error.response.data);
      } else if (error.message) {
        errorContent = `Error: ${error.message}`;
      }
      
      // Create error message
      const errorMessage = {
        id: messages.length + 2,
        type: 'assistant',
        content: errorContent,
        isError: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const handleSuggestedPrompt = (prompt) => {
    setInput(prompt);
  };

  return (
    <ChatContainer>
      <ChatHeader>Chain3 AI Blockchain Assistant</ChatHeader>
      <MessagesContainer>
        {messages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}
        <div ref={messagesEndRef} />
      </MessagesContainer>
      
      {messages.length <= 2 && (
        <SuggestedPromptsContainer>
          {suggestedPrompts.map((prompt, index) => (
            <SuggestedPrompt 
              key={index} 
              onClick={() => handleSuggestedPrompt(prompt)}
            >
              {prompt}
            </SuggestedPrompt>
          ))}
        </SuggestedPromptsContainer>
      )}
      
      <InputContainer>
        <ChatInput
          type="text"
          placeholder="Type a query (e.g., 'What's the balance of this wallet: 0x1234...')"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
        />
        <SendButton onClick={handleSendMessage} disabled={isLoading || !input.trim()}>
          {isLoading ? 'Processing...' : <><FiSend /> Send</>}
        </SendButton>
      </InputContainer>
    </ChatContainer>
  );
}

export default ChatInterface;
